import { USE_OUT_OF_CORE_TILE_MANAGER, USE_WBOIT } from "../globals";
import { FrustumIntersector } from './FrustumIntersector';
import THREE from "three";
import { Int32MaxHeap } from './Int32MaxHeap.js';
import { OutOfCoreTileManager } from './out-of-core-tile-manager/OutOfCoreTileManager.js';
import { StageNames } from './out-of-core-tile-manager/tasks/defaultStageConfiguration';
import { OtgLoader } from "../../file-loaders/main/OtgLoader";
import { RcsFileLoader } from "../../file-loaders/rcs/RcsFileLoader";
import { RenderBatch } from "./RenderBatch";

/** @import { SpatialTree, TreeNodes } from "./SpatialTreeInterface" */
/** @import { RenderModel } from "./RenderModel" */
/** @import { FragmentList } from "../scene/FragmentList" */

/*
 * Keep these threeJs objects file local for performance reasons,
 * as these are changed frequently, so we keep expensive object creation minimal.
 */
const tmpBox = new THREE.Box3();
const tmpBox2 = new THREE.Box3();
const tmpVec = new THREE.Vector3();
const sqrt3 = Math.sqrt(3);

export class ModelIteratorBVH
{
  // _bvhRenderBatches is a sparse array of RenderBatches, each RenderBatch has a few fragments.
  // Only those elements in the array that have a RenderBatch are defined.
  /** @type {RenderBatch[]} */#bvhRenderBatches;

  // What is the containment state of this node, if known? Is either CONTAINMENT_UNKNOWN
  // or INTERSECTS or CONTAINS. If CONTAINS, we don't have to run the frustum cull
  // test, saving a few percent in speed.
  #bvhContainment;

  /**
   * Traversal state. The heap and the LIFO stack are never simultaneously non-empty, so they
   * share the same Int32 backing buffer:
   *   - With WBOIT:  only the heap is ever populated (opaque + transparent batches, ordered by
   *                  screen-space error).
   *   - Without WBOIT: the heap is populated and drained for the opaque subtree, then the stack
   *                    is populated and drained for the transparent subtree.
   * @type {Int32Array}
   */
  #bvhTraversalBuffer;
  /** @type {Int32MaxHeap} */
  #bvhHeap;
  #bvhStackSize;

  #bvhNodeScreenspaceErrors;
  #bvhOpaqueDone;

  /** @type {OutOfCoreTileManager|undefined} */#outOfCoreTileManager;

  // true if nodes from transparent tree should be ignored for rendering.
  #bvhIgnoreTransparency;

  // WBOIT: whether reset() primes the transparent subtree root (node 1) into the combined heap so transparents
  // traverse interleaved with opaques. Set via setDrawTransparents() rather than a reset() arg, because the leaflet
  // iterator's reset(frustum, camera, viewIndex) uses the 3rd positional slot for something else.
  #drawTransparents = true;

  #frustum;
  #done;
  #RenderBatch;
  #resetVisStatus;
  #wantOoctm;

  // With #raycastOnly, the constructor will not initialize the RenderBatches. Since the
  // inner nodes are empty, bboxes of the RenderBatches and the bvh would be the same. Also,
  // the hidden bounding box and every other property of the RenderBatches wouldn't be used, wasting memory.
  // With this enabled, only rayCast and updateBoundsRaycastOnly are supported.
  /** @type {boolean} */#raycastOnly;

  /** @type {?RenderModel} */#renderModel;

  /** @type {?number} */#modelIteratorId;

  /** This factor is multiplied to the screen space error that is reported to the BVH iterator. At the moment, we use
   *  only the viewport size as scaling factor */
  #screenSpaceErrorScalingFactor = 1.0;

  /** @type {?SpatialTree} */#bvh;

  /** @type {boolean} */#isRcsPointCloud;

  /**
   * Initializes the model iterator with the given parameters.
   * @param {RenderModel} renderModel - The render model for which this iterator is being created
   * @param {SpatialTree} bvh - See SpatialTreeInterface.js
   * @param {boolean} [initializeOoctm=true] - Whether to initialize the OutOfCoreTileManager. Should be set for non-consolidated BVHs.
   * @param {boolean} [raycastOnly=false] - Whether the iterator will be used only for raycasting.
   */
  constructor(renderModel, bvh, initializeOoctm = true, raycastOnly = false) {
    this.#bvh = bvh;
    this.#renderModel = renderModel;
    this.#raycastOnly = raycastOnly;
    this.#isRcsPointCloud = renderModel.loader instanceof RcsFileLoader;
    if (this.#raycastOnly) {
      return;
    }

    this.#bvhOpaqueDone = false;
    this.#bvhIgnoreTransparency = false;
    this.#frustum = null;
    this.#done = false;
    this.#resetVisStatus = true;
    this.#modelIteratorId = null;

    // Take the RenderBatch class from the model, so on demand loading can
    // use a different class to handle redraws properly
    this.#RenderBatch = renderModel.RenderBatch;
    this.#wantOoctm = USE_OUT_OF_CORE_TILE_MANAGER && initializeOoctm;

    const nodes = bvh.nodes;

    this.#bvhRenderBatches = new Array(nodes.nodeCount);
    this.#bvhContainment = new Int8Array(nodes.nodeCount);

    this.#bvhNodeScreenspaceErrors = new Float32Array(nodes.nodeCount);

    // Note that the buffer is shared by heap and stack -> they must never be simultaneously non-empty.
    this.#bvhTraversalBuffer = new Int32Array(nodes.nodeCount + 1);
    this.#bvhHeap = new Int32MaxHeap(this.#bvhTraversalBuffer, this.#bvhNodeScreenspaceErrors);
    this.#bvhStackSize = 0;

    for (let i = 0; i < nodes.nodeCount; i++) {

      // does this node have real objects in it?
      const primCount = nodes.getPrimCount(i);
      if (primCount === 0) {
        continue;
      }

      const largestFragmentSize = nodes.getLargestPrimitiveSize(i);
      const frags = this.#renderModel.getFragmentList();
      this.#bvhRenderBatches[i] = new this.#RenderBatch(this, frags, nodes.getPrimStart(i), primCount, undefined, largestFragmentSize, this.#bvh.options?.sortFuncType);

      const currentRenderBatch = this.#bvhRenderBatches[i];

      // These are set manually, because we will not be adding fragments to the
      // render batch one by one -- the fragments are already loaded.
      currentRenderBatch.lastItem = currentRenderBatch.start + primCount;
      currentRenderBatch.numAdded = primCount;
      currentRenderBatch.numAddedTotal = primCount;
      currentRenderBatch.nodeIndex = i;

      if (nodes.isNodeTransparent(i)) {
        currentRenderBatch.sortObjects = true; //scene contains transparent objects
      }

      nodes.getBoxArray(i, currentRenderBatch.bboxes);
    }
  }

  dtor() {
    this.#RenderBatch = null;
    this.#renderModel = null;

    this.#outOfCoreTileManager?.releaseIterator(this.#modelIteratorId);
  }

  unregisterOutOfCoreTileManager() {
    this.#outOfCoreTileManager = undefined;
  }

  /**
   * WBOIT only: when true, the next reset() primes the transparent subtree root (node 1) into the combined heap so
   * transparent batches traverse interleaved with opaques. When false the root is not primed (settled-view accum
   * reuse or non-progressive renders) and the correction pass via resetTransparents() handles transparents instead.
   * @param {boolean} drawTransparents
   */
  setDrawTransparents(drawTransparents) {
    this.#drawTransparents = drawTransparents;
  }

  /**
   * Reset the iterator for the given frustum and camera.
   * @param {FrustumIntersector} frustum - The frustum
   * @param {THREE.Camera} camera - The camera
   */
  reset(frustum, camera) {
    this.#frustum = frustum;

    // We scale the errors from the BVH iterator by the projection scale of the frustum
    // to take the size of the viewport into account when comparing the errors from different
    // viewports with each other in the OutOfCoreTileManager.
    // TODO: How should we handle the case when the camera is orthographic? We don't compute
    // a real orthographic screen space error, but divide by distance also in orthographic
    // views, because we consider closer objects more important, even if they do not occupy more
    // pixels. However, this poses a problem when comparing errors from orthographic and perspective
    // views because the errors for the orthographic view do not correspond to pixel sizes at all.
    // The projection factor used here, only gives the correct result for objects at a distance of 1
    // from the camera. We can use a different reference distance for orthographic views by scaling the error,
    // but it is unclear which reference distance we should use for this.
    this.#screenSpaceErrorScalingFactor = frustum.projScale;

    this.#bvhHeap.clear();
    this.#bvhStackSize = 0;

    // means "unknown containment state"
    this.#bvhContainment[0] = this.#bvhContainment[1] = FrustumIntersector.CONTAINMENT_UNKNOWN;

    // Prime the heap with the root of the opaque subtree (node 0).
    // With WBOIT, also insert the root of the transparent subtree (node 1) so that
    // opaque and transparent batches are traversed together in a single pass.
    //
    // We don't use the heap's push() here so the heap property isn't satisfied between the roots and the first
    // draw will always be the opaque root. After this draw it is a proper heap as it has only one element.
    // This mirrors the behavior prior to introduction of the heap (whether it is by design or not).
    //
    // Note also that updateNodeArea(0/1) hasn't been used to compute the root's error/area yet,
    // so push() wouldn't work correctly if called here.
    this.#bvhTraversalBuffer[this.#bvhHeap.size++] = 0;
    if (USE_WBOIT && this.#drawTransparents) {
      // Include transparent root (node 1) in the combined heap so transparents are drawn interleaved with
      // opaques during the progressive pass; the correction pass via resetTransparents() later redraws them
      // against complete depth. #drawTransparents is false when reusing the previous frame's accum (same view)
      // or in non-progressive renders, where the correction pass handles them.
      this.#bvhTraversalBuffer[this.#bvhHeap.size++] = 1;
    }

    this.#bvhOpaqueDone = false;
    this.#done = false;

    if (this.#resetVisStatus) {
      let scenes = this.#bvhRenderBatches;
      let len = scenes.length;
      for (let i = 0; i < len; ++i) {
        let scene = scenes[i];
        if (scene && scene.resetVisStatus) {
          scene.resetVisStatus();
        }
      }
      this.#resetVisStatus = false;
    }

    // The out of core manager maintains a counter for the current frame (full frame, not each individual pr
    // progressive frame) to determine whether an update of the screen space error is still valid. We increment
    // this counter here every time the traversal is being reset.
    this.#outOfCoreTileManager?.incrementFrameCount(this.#modelIteratorId);

    // Initialize the out of core tile manager and compute initial screen space errors to have proper task prioritization
    // This is done here lazily, because before the first reset there's no frustum and thus no useful priorities
    if (this.#wantOoctm && !this.#outOfCoreTileManager) {
      this.#outOfCoreTileManager = OutOfCoreTileManager.getManagerForModel(this.#renderModel);
      this.#modelIteratorId = this.#outOfCoreTileManager.getIteratorId(this);

      this.#bvhRenderBatches.forEach((renderBatch, index) => {
        const isTransparent = this.#bvh.nodes.isNodeTransparent(index);

        this.#outOfCoreTileManager.initializeBvhNode(index, isTransparent);
        this.updateNodeArea(index);
      });
    }

    // The screen area of the root nodes (for opaques and transparents) is never computed
    // during traversal. Therefore, we trigger the update already here.
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      this.updateNodeArea(0);
      this.updateNodeArea(1);
    }
  }

  /**
   * Resets the traversal and sets it up to re-traverse only the transparent subtree.
   * Should only be called after all opaque batches have been traversed, as they won't be visited again.
   * Only intended for use with WBOIT.
   */
  resetTransparents() {
    this.#bvhHeap.clear();
    this.#bvhStackSize = 0;
    // The transparent subtree is traversed via the heap with WBOIT.
    console.assert(USE_WBOIT, "resetTransparents should only be called when WBOIT is used");
    this.#bvhTraversalBuffer[this.#bvhHeap.size++] = 1;
  }

  /**
   * Check if traversal of the opaque subtree has finished and start
   * traversing the transparent subtree if appropriate.
   *
   * When using WBOIT, this should not be called.
   */
  _checkOpaqueCompletion() {
    if (!this.#bvhOpaqueDone && this.#bvhHeap.size === 0) {
      // In case we want to skip all nodes in the transparent subtree.
      if (!this.#bvhIgnoreTransparency) {
        // The transparent subtree is traversed back-to-front (LIFO via the view-direction
        // split flags), so push the transparent root onto the stack rather than the heap.
        // Note that the heap is known empty here (outer condition) and we can safely start using the stack.
        this.#bvhTraversalBuffer[this.#bvhStackSize++] = 1; // root of transparent subtree is at index 1
      }
      this.#bvhOpaqueDone = true;
    }
  }

  nextBatch() {
    if (!USE_WBOIT) {
      this._checkOpaqueCompletion();
    }

    // The traversal state lives in #bvhTraversalBuffer, used as either a max-heap or a LIFO stack but
    // never both at once (#bvhHeap.size and #bvhStackSize are never simultaneously non-zero):
    //   - As a heap: ordered by screen-space error. Used for the opaque subtree (large-area-first)
    //     and, with WBOIT, also for the transparent subtree.
    //   - As a stack: LIFO. Used for the transparent subtree without WBOIT (back-to-front via the
    //     view-direction split flags).
    //
    // The loop ends either when both are empty or when a render batch is found and returned.
    // The next call to nextBatch picks up where this one left off.
    // Note: render batches can and do get returned before the whole tree is traversed,
    // because they can also be attached to inner nodes. This means there may be nodes with
    // larger screen areas that come later on.
    while (this.#bvhHeap.size !== 0 || this.#bvhStackSize !== 0) {
      // Get a node from either heap or stack. WBOIT only uses the heap;
      // non-WBOIT fully drains the heap (opaque) before any push to the stack (transparent).
      const nodeIdx = this.#bvhHeap.size !== 0 ?
      this.#bvhHeap.pop() :
      this.#bvhTraversalBuffer[--this.#bvhStackSize];

      // Is box already found to be contained? This happens when a box's parent is fully contained.
      // We can then avoid the frustum test.
      let intersects = this.#bvhContainment[nodeIdx];
      if (intersects !== FrustumIntersector.CONTAINS) {
        // could be outside or intersecting, so do test
        this.#bvh.nodes.getBoxThree(nodeIdx, tmpBox);
        intersects = this.#frustum.intersectsBox(tmpBox);
      }

      // Node is entirely outside, go on to the next node
      if (intersects !== FrustumIntersector.OUTSIDE) {
        const childCount = this.#bvh.nodes.getChildCount(nodeIdx);

        // Is it inner node? Add children for processing.
        if (childCount > 0) {
          // Two child-ordering strategies:
          //   - Opaque subtree (and, with WBOIT, the transparent subtree too): sort by
          //     screen-space area via the heap so the largest-area child is visited first.
          //   - Transparent subtree without WBOIT: traverse back-to-front via the LIFO
          //     stack using the view-direction split flags below.
          if (USE_WBOIT || !this.#bvhOpaqueDone) {
            // Traversal based on visible screen area: compute area for each child
            // and insert into the priority queue. The heap maintains ordering.
            // Also used for N-ary trees where view-direction-based ordering is
            // not available (no split-axis flags).
            for (let childIdx = 0; childIdx < childCount; childIdx++) {
              const childNodeIdx = this.#bvh.nodes.getChildIndex(nodeIdx, childIdx);
              let area;

              // When using HLOD (without out of core) we currently use hlod traversal with the initial unconsolidated BVH. Its bvh options doesn't
              // have the sortFuncType and therefore falls back to the box area one.
              // By checking for this flag, we avoid using the hlod traversal for non-hlod bvhs
              if (USE_OUT_OF_CORE_TILE_MANAGER && this.#bvh.options.sortFuncType) {
                area = this.updateNodeArea(childNodeIdx);
              } else {
                this.#bvh.nodes.getBoxThree(childNodeIdx, tmpBox);
                area = this.#frustum.projectedBoxArea(tmpBox, intersects === FrustumIntersector.CONTAINS);
                this.#bvhNodeScreenspaceErrors[childNodeIdx] = area;
              }

              // "worst case" containment is recorded for later examination.
              this.#bvhContainment[childNodeIdx] = intersects;

              // Insert into the heap so the largest-area node is always popped first.
              // Skip nodes outside the frustum (area === 0) — their descendants are
              // also outside, so there's nothing to traverse.
              if (area > 0) {
                this.#bvhHeap.push(childNodeIdx);
              }

            }

          } else {

            if (!this.#bvh.isBinaryTree) {
              throw new Error("View-direction traversal is not yet supported for non-binary trees.");
            }

            // Binary tree: traversal by view direction via the LIFO stack.
            // Reverse order if looking in the negative of the child split axis.
            // Reverse order if we are traversing last first (depthFirst is always 1 here).
            // If node contains transparent objects, then reverse the result so we traverse back to front.
            // In other words, reverse the order if an odd number of flags are true.
            const flags = this.#bvh.nodes.getFlags(nodeIdx);
            const reverseAxis = this.#frustum.viewDir[flags & 3] < 0 ? 1 : 0;
            let firstChild = flags >> 2 & 1;
            const transparent = flags >> 3 & 1;
            const depthFirst = 1;

            if (reverseAxis ^ depthFirst ^ transparent) {
              firstChild = 1 - firstChild;
            }

            const child = this.#bvh.nodes.getLeftChild(nodeIdx);
            const firstIdx = child + firstChild;
            const secondIdx = child + (1 - firstChild);

            // "worst case" containment is recorded for later examination.
            this.#bvhContainment[firstIdx] = this.#bvhContainment[secondIdx] = intersects;

            // Push firstIdx first, secondIdx second: with LIFO the latter pops first,
            // giving the desired view-ordered traversal.
            this.#bvhTraversalBuffer[this.#bvhStackSize++] = firstIdx;
            this.#bvhTraversalBuffer[this.#bvhStackSize++] = secondIdx;

            // Update the cost for transparent nodes.
            if (this.#outOfCoreTileManager) {
              this.updateNodeArea(firstIdx);
              this.updateNodeArea(secondIdx);
            } else {
              this.#bvhNodeScreenspaceErrors[firstIdx] = -1;
              this.#bvhNodeScreenspaceErrors[secondIdx] = -1;
            }
          }
        }

        // Are there graphics in the node? Then return its scene, i.e. its RenderBatch.
        // Inner nodes with children can and do have render batches of their own.
        // This works against a pure screen=area or front-to-back ordering, as
        // these fragments will always get returned first, before further traversal of the tree.
        const prims = this.#bvh.nodes.getPrimCount(nodeIdx);
        if (prims !== 0) {
          const renderBatch = this.#bvhRenderBatches[nodeIdx];

          if (!USE_WBOIT && renderBatch.sortObjects) {// Transparent nodes
            // We use a negative cost for transparent node to make sure they come after all opaque nodes
            // in the queue. The cost is the inverse of the distance to the camera, because we want to render
            // transparent nodes in back-to-front order.
            let distance = renderBatch.getBoundingBox(tmpBox).getBoundingSphere().center.distanceTo(this.#frustum.eye);
            renderBatch.renderImportance = -(1 / distance);
          } else {
            if (USE_OUT_OF_CORE_TILE_MANAGER) {
              // If the out of core tile manager is used, the nodes will be sorted based on the the node area
              // and we should use the same measure for the render importance that is used to decide which
              // batch to use next when doing multi-model rendering.
              renderBatch.renderImportance = this.#bvhNodeScreenspaceErrors[nodeIdx];
            } else {
              renderBatch.renderImportance = this.#frustum.projectedBoxArea(renderBatch.getBoundingBox(), intersects === FrustumIntersector.CONTAINS);
            }
          }

          // Frustum culling for the RenderBatch is done in RenderBatch.applyVisibility,
          // so we don't need it here.
          // Just returning the batch and it will get cull checked later.
          return renderBatch;
        }
      }

      if (!USE_WBOIT) {
        this._checkOpaqueCompletion();
      }
    }

    this.#done = true;
    return null;
  }

  /**
   * Updates the screen-space-error proxy for a BVH node and reports it to the OutOfCoreTileManager (if present).
   * @param {number} index - Index of the node
   * @returns {number} - area of the node or -1 if area could not be computed
   */
  updateNodeArea(index) {
    let area;
    let renderBatch = this.#bvhRenderBatches[index];

    // Skip empty nodes that don't have a render batch.
    // Point clouds (RCS) may have empty inner nodes
    if (!renderBatch && !this.#isRcsPointCloud) {
      return -1;
    }

    if (this.#bvh.options.sortFuncType === Autodesk.Viewing.Private.BVHSortFuncType.DIAMETER) {
      // Use the bounding sphere radius of the largest fragment in RenderBatch divided by the distance to the RenderBatch bounding box
      const largestFragDiameterSquared = this.#bvh.nodes.getLargestPrimitiveSize(index);
      const largestFragRadius = Math.sqrt(largestFragDiameterSquared) * 0.5;
      area = largestFragRadius;

      if (this.#frustum.perspective) {
        this.#bvh.nodes.getBoxThree(index, tmpBox);

        // We clamp the distance to still order by largest object size if we are inside the bounding box
        area = largestFragRadius / Math.max(1, tmpBox.distanceToPoint(this.#frustum.eye));
      }
    } else {
      // Use the bounding box area of the largest fragment in RenderBatch divided by the squared distance to the RenderBatch bounding box
      area = this.#bvh.nodes.getLargestPrimitiveSize(index);

      if (this.#frustum.perspective) {
        this.#bvh.nodes.getBoxThree(index, tmpBox);
        let distSquared = tmpBox.distanceToPoint(this.#frustum.eye);
        distSquared *= distSquared;
        area = area / Math.max(1, distSquared);
      }
    }

    this.#bvhNodeScreenspaceErrors[index] = area;

    // Report cost to the OutOfCoreTileManager.
    this.#outOfCoreTileManager?.updateBvhNodeScreenSpaceError(this.#modelIteratorId, index,
    this.#screenSpaceErrorScalingFactor * area);
    return area;
  }

  setIgnoreTransparency(ignore) {
    this.#bvhIgnoreTransparency = ignore;
  }

  getIgnoreTransparency() {
    return this.#bvhIgnoreTransparency;
  }

  /**
   * Updates the bounding box of a BVH node
   * @param {number} [nodeIdx] The ID of the node
   * @param {boolean} [updateChildren=true] - Also update the children of this node
   * @param {boolean} [updateParents=false] - Recursively update all the parents of this node
   *                                  (note this will not recompute the Bounding Box for the other children of the parents, but only make
   *                                   sure they contain the updated bounding box of this node)
   */
  #updateBVHRec(nodeIdx, updateChildren = true, updateParents = false) {
    const childCount = this.#bvh.nodes.getChildCount(nodeIdx);
    const hasChildren = childCount > 0;
    if (hasChildren && updateChildren) {
      for (let childIdx = 0; childIdx < childCount; childIdx++) {
        this.#updateBVHRec(this.#bvh.nodes.getChildIndex(nodeIdx, childIdx));
      }
    }

    // leaf nodes of raycast iterators don't need any update
    if (!this.#raycastOnly || hasChildren) {
      tmpBox.makeEmpty();

      if (hasChildren) {
        for (let childIdx = 0; childIdx < childCount; childIdx++) {
          this.#bvh.nodes.getBoxThree(this.#bvh.nodes.getChildIndex(nodeIdx, childIdx), tmpBox2);
          tmpBox.union(tmpBox2);
        }
      }

      if (!this.#raycastOnly && this.#bvh.nodes.getPrimCount(nodeIdx) > 0) {
        tmpBox.union(this.#bvhRenderBatches[nodeIdx].getBoundingBox());
        tmpBox.union(this.#bvhRenderBatches[nodeIdx].getBoundingBoxHidden());
      }

      this.#bvh.nodes.setBoxThree(nodeIdx, tmpBox);
    }

    const parent = this.#bvh.getNodeParent(nodeIdx);
    if (parent !== -1 && updateParents) {
      this.#updateBVHRec(parent, false, true);
    }
  }

  fragmentBoundsChanged(fragId) {
    const nodeIndex = this.#bvh.fragId2nodeId[fragId];
    this.#bvhRenderBatches[nodeIndex]?.invalidateBBox();
  }

  /**
   * Updates the bounds of a BVH node
   * @param {number} nodeIdx - The ID of the node
   * @param {boolean} forceUpdate - If false, we only update bounds for RenderBatches with bboxDirty = true
   */
  updateBounds(nodeIdx, forceUpdate = true) {
    this.#renderModel.iteratorChangedBounds();

    if (nodeIdx !== undefined) {
      this.#bvhRenderBatches[nodeIdx].updateBounds();
      this.#updateBVHRec(nodeIdx, false, true);
      return;
    }

    // Update the bounds of all render batches
    let isOtgLoader = this.#renderModel.loader instanceof OtgLoader;
    for (let i = 0; i < this.#bvhRenderBatches.length; i++) {
      let node = this.#outOfCoreTileManager?.getBvhNode(i);
      const renderBatch = this.#bvhRenderBatches[i];

      // With the OtgLoader, the fraglist might not be loaded yet, and until then the the bounds
      // of a node must not be updated because the fragment's bounding boxes are all zeroes.
      // So until the OOCTM nodes are initialized and the node's fraglist is loaded, we skip this.
      // We can safely skip this because LoadFraglistTask will call updateBounds() again.
      if (isOtgLoader && (this.#wantOoctm && !this.#outOfCoreTileManager ||
      node && !node.isStageCompleted(StageNames.FRAGLIST_LOADED))) {
        continue;
      }

      if (renderBatch) {
        const changed = renderBatch.updateBounds(forceUpdate);
        // Propagate change upwards for selective update. For forced update,
        // we skip this and just traverse the whole tree once after the loop.
        if (!forceUpdate && changed) {
          this.#updateBVHRec(i, false, true);
        }
      }
    }

    if (forceUpdate) {
      //Also update all bounding volume tree nodes' bounds.
      //If objects move too much this will make the BVH less effective.
      //However, this only happens during explode or animation, so it shouldn't
      //be an issue. We can always rebuild the BVH in case objects really move a lot.
      this.#updateBVHRec(0); //opaque root
      this.#updateBVHRec(1); //transparent root
    }
  }

  updateBoundsRaycastOnly() {
    console.assert(this.#raycastOnly, "updateBoundsRaycastOnly can only be called if raycastOnly is true");
    const frags = this.#renderModel.getFragmentList();
    const primArray = this.#bvh.primitives;

    for (let i = 0; i < this.#bvh.nodes.nodeCount; i++) {
      if (this.#bvh.nodes.getPrimCount(i) === 0) {
        continue;
      }

      const start = this.#bvh.nodes.getPrimStart(i);
      const end = start + this.#bvh.nodes.getPrimCount(i);
      tmpBox.makeEmpty();

      for (let j = start; j < end; j++) {
        const fragId = primArray[j];
        frags.getWorldBounds(fragId, tmpBox2);
        if (frags.isLine(fragId)) {
          // Expand bounding box a bit, to take into account axis aligned lines
          tmpBox2.expandByScalar(0.5);
        }
        tmpBox.union(tmpBox2);
      }
      this.#bvh.nodes.setBoxThree(i, tmpBox);
    }

    // update all bounding volume tree nodes' bounds.
    this.#updateBVHRec(0); // opaque root
    this.#updateBVHRec(1); // transparent root
  }


  getBounds(visibleBounds, hiddenBounds) {
    for (const batch of this.#bvhRenderBatches) {
      if (!batch) {
        continue;
      }

      visibleBounds.union(batch.getBoundingBox());
      hiddenBounds.union(batch.getBoundingBoxHidden());
    }
  }

  rayCast(raycaster, intersectsOut, dbIdFilter, options = {}) {
    const rayOrigin = raycaster.ray.origin;
    let nodeStack = [1, 0];
    let pt = new THREE.Vector3();
    let nodeBBox = new THREE.Box3();

    let currentRenderBatch;
    if (this.#raycastOnly) {
      // To still be able to re-use RenderBatch.raycast, this scratch RenderBatch is used.
      currentRenderBatch = new RenderBatch(null, this.#renderModel.getFragmentList(), -1, -1, this.#bvh.primitives);
      currentRenderBatch.bboxes = null;
    }

    // TODO when only the nearest hit is needed (no intersectsOut),
    // we can be much more efficient than iterating through all nodes.
    while (nodeStack.length > 0) {
      const nodeIdx = nodeStack.pop();

      this.#bvh.nodes.getBoxThree(nodeIdx, nodeBBox);

      if (options.maxDistance != undefined) {
        const distanceToPoint = nodeBBox.distanceToPoint(rayOrigin);
        if (distanceToPoint > options.maxDistance) {
          continue;
        }
      }

      const intersectionPoint = raycaster.ray.intersectBox(nodeBBox, pt);
      if (intersectionPoint === null) {
        continue;
      }

      const childCount = this.#bvh.nodes.getChildCount(nodeIdx);
      for (let childIdx = 0; childIdx < childCount; childIdx++) {
        nodeStack.push(this.#bvh.nodes.getChildIndex(nodeIdx, childIdx));
      }

      const prims = this.#bvh.nodes.getPrimCount(nodeIdx);
      if (prims !== 0) {
        if (this.#raycastOnly) {
          currentRenderBatch.start = this.#bvh.nodes.getPrimStart(nodeIdx);
          currentRenderBatch.lastItem = currentRenderBatch.start + this.#bvh.nodes.getPrimCount(nodeIdx);
        } else {
          currentRenderBatch = this.#bvhRenderBatches[nodeIdx];
        }
        currentRenderBatch.raycast(raycaster, intersectsOut, dbIdFilter, options);
      }
    }
  }

  intersectFrustum(frustumIntersector, callback) {
    let nodeStack = [1, FrustumIntersector.CONTAINMENT_UNKNOWN, 0, FrustumIntersector.CONTAINMENT_UNKNOWN];

    while (nodeStack.length) {

      const parentIntersectionState = nodeStack.pop();
      const nodeIdx = nodeStack.pop();

      //Check if current BVH node intersects the frustum. Take into account
      //the intersection state of the parent node, in case we can short-circuit the frustum check
      //when containment is known.
      let result;
      if (parentIntersectionState === FrustumIntersector.CONTAINS) {
        result = FrustumIntersector.CONTAINS;
      } else {
        this.#bvh.nodes.getBoxThree(nodeIdx, tmpBox);
        result = frustumIntersector.intersectsBox(tmpBox);
      }

      if (result === FrustumIntersector.OUTSIDE) {
        continue;
      }

      const childCount = this.#bvh.nodes.getChildCount(nodeIdx);
      for (let childIdx = 0; childIdx < childCount; childIdx++) {
        nodeStack.push(this.#bvh.nodes.getChildIndex(nodeIdx, childIdx));
        nodeStack.push(result);
      }

      const prims = this.#bvh.nodes.getPrimCount(nodeIdx);
      if (prims !== 0) {
        let scene = this.#bvhRenderBatches[nodeIdx];
        scene && scene.intersectFrustum(frustumIntersector, callback, result === FrustumIntersector.CONTAINS);
      }
    }
  }

  getSceneCount() {
    return this.#bvhRenderBatches.length;
  }

  getGeomScenes() {
    return this.#bvhRenderBatches;
  }

  getOutOfCoreTileManager() {
    return this.#outOfCoreTileManager;
  }

  getBVH() {
    return this.#bvh;
  }

  getInstanceOrder() {
    return this.#bvh.primitives;
  }

  done() {
    // not supported (yet)
    // Seems to be needed for search in gui/controls only. let's check if this information is required to be public.
    return this.#done;
  }

  resetVisStatus() {
    this.#resetVisStatus = true;
  }

  clone() {
    const bvhCopy = this.#bvh.createSortableReference();
    return new ModelIteratorBVH(this.#renderModel, bvhCopy, this.#wantOoctm);
  }

  /**
   * Return the ID which identifies the model iterator in the out of core tile manager.
   * @return {number} the id
   */
  getIteratorOutOfCoreManagerId() {
    return this.#modelIteratorId;
  }


  /**
   * @returns {boolean} true, if the OutOfCoreTileManager is supposed to be used.
   */
  wantOoctm() {
    return this.#wantOoctm;
  }

  /**
   * @param {number} fragId
   * @returns {RenderBatch|undefined}
   */
  getInstanceBatch(fragId) {
    const nodeIndex = this.#bvh.fragId2nodeId[fragId];
    // Get the render batch for this node
    return this.#bvhRenderBatches[nodeIndex];
  }
};